import React, {useState} from 'react'
import {Input} from '../../../shared/Input'
import {
  ONBOARDING_CONTACT_FIELDS,
  validateOnboardingContact,
} from '../utils/onboardingContactValidation'
import {
  OnboardingContactWrapper,
  OnboardingContactHeaderRow,
  OnboardingContactTitle,
  OnboardingContactCheckboxRow,
  OnboardingContactFieldsGrid,
  OnboardingContactColumn,
} from './OnboardingContact.styles'

// Who the CSM should call/email for the scheduled call, defaulting to the
// account owner. Ports the old flow's onboarding-program screen's "Onboarding
// Contact" name/email/phone fields + "Same as account owner" checkbox (see
// PurchaseLinkSlice's onboardingContact / useOnboardingScheduling's
// handleScheduleCall) — built with the same field-config + validate-on-blur
// structure as AccountInfo's single-/multi-location forms (see
// utils/onboardingContactValidation.js and AccountInfo/utils/formHelpers.js).
//
// contact: {useAccountOwner, firstName, lastName, email, mobile} — the raw
//   PurchaseLinkSlice.onboardingContact value.
// resolvedContact: {firstName, lastName, email, mobile} — the account
//   owner's info, shown (read-only) whenever useAccountOwner is true.
// country: accountInfo.country, for country-aware phone validation.
// locked: true once any call has been scheduled for this order — the
//   contact has already gone out via plans/onboarding (and mirrored onto
//   order_header, see useOnboardingScheduling's syncOrderHeaderAppointment),
//   so further edits here would silently drift from what was actually sent.
//   Disables every field AND the checkbox itself, regardless of
//   useAccountOwner.
export function OnboardingContact({
  contact,
  resolvedContact,
  country,
  locked = false,
  onFieldChange,
  onToggleUseAccountOwner,
}) {
  // Only tracked (and only shown) while the rep is actually editing a custom
  // contact — the account-owner-sourced fields are disabled/read-only here,
  // so there's nothing for the rep to fix on them even if one were somehow
  // incomplete (the Schedule Call button's own gate still catches that case).
  const [touched, setTouched] = useState({})

  const displayValues = contact.useAccountOwner ? resolvedContact : contact
  const errors = contact.useAccountOwner
    ? {}
    : validateOnboardingContact(displayValues, country)

  const handleBlur = key => setTouched(prev => ({...prev, [key]: true}))

  const [nameFields, contactFields] = [
    ONBOARDING_CONTACT_FIELDS.slice(0, 2),
    ONBOARDING_CONTACT_FIELDS.slice(2),
  ]

  const renderField = field => (
    <Input
      key={field.key}
      id={`onboarding-contact-${field.key}`}
      label={field.label}
      type={field.type}
      size="sm"
      variant="form"
      required
      disabled={locked || contact.useAccountOwner}
      value={displayValues[field.key] || ''}
      errorText={touched[field.key] ? errors[field.key] : undefined}
      onChange={e => onFieldChange(field.key, e.target.value)}
      onBlur={() => handleBlur(field.key)}
      placeholder={field.label}
    />
  )

  return (
    <OnboardingContactWrapper>
      <OnboardingContactHeaderRow>
        <OnboardingContactTitle>Onboarding Contact</OnboardingContactTitle>
        <OnboardingContactCheckboxRow>
          <input
            type="checkbox"
            checked={contact.useAccountOwner}
            disabled={locked}
            onChange={e => onToggleUseAccountOwner(e.target.checked)}
          />
          Same as account owner
        </OnboardingContactCheckboxRow>
      </OnboardingContactHeaderRow>

      <OnboardingContactFieldsGrid>
        <OnboardingContactColumn>
          {nameFields.map(renderField)}
        </OnboardingContactColumn>
        <OnboardingContactColumn>
          {contactFields.map(renderField)}
        </OnboardingContactColumn>
      </OnboardingContactFieldsGrid>
    </OnboardingContactWrapper>
  )
}
